import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertCircle,
  FiArrowRight,
  FiBox,
  FiClock,
  FiMapPin,
  FiSearch,
} from "react-icons/fi";

import { trackingApi } from "../services/trackingApi";
import {
  getDestinationPort,
  getEta,
  getOriginPort,
  getTrackingNo,
  getTrackingStatus,
  getVesselName,
} from "../utils/shipsGoTracking";

const extractRows = (response) => {
  const data = response?.data;

  if (Array.isArray(data?.shipments)) return data.shipments;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;

  return [];
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusBadgeClass = (statusGroup) => {
  if (statusGroup === "delivered") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
  }

  if (statusGroup === "arrived") {
    return "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/70 dark:bg-violet-950/40 dark:text-violet-300";
  }

  return "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/70 dark:bg-blue-950/40 dark:text-blue-300";
};

export default function TrackShipment() {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [recentShipments, setRecentShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadRecent = async () => {
      try {
        const response = await trackingApi.list();
        setRecentShipments(extractRows(response).slice(0, 5));
      } catch (loadError) {
        console.error("RECENT TRACKING LOAD ERROR:", loadError);
      }
    };

    loadRecent();
  }, []);

  const recentNumbers = useMemo(
    () => recentShipments.map((shipment) => getTrackingNo(shipment)),
    [recentShipments],
  );

  const handleSearch = async (event) => {
    event.preventDefault();

    const cleanQuery = query.trim();
    if (!cleanQuery) return;

    setLoading(true);
    setSearched(true);
    setError("");

    try {
      const response = await trackingApi.list(
        `?search=${encodeURIComponent(cleanQuery)}`,
      );
      const rows = extractRows(response);

      setResults(rows);

      if (rows.length === 1) {
        navigate(`/tracking?shipment=${rows[0].id}`);
      }
    } catch (searchError) {
      console.error("TRACK SHIPMENT SEARCH ERROR:", searchError);
      setResults([]);
      setError(
        searchError.response?.data?.message ||
          "Unable to search tracking records.",
      );
    } finally {
      setLoading(false);
    }
  };

  const openShipment = (shipment) => {
    navigate(`/tracking?shipment=${shipment.id}`);
  };

  return (
    <div className="p-6 text-slate-900 dark:text-slate-100">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-50 text-xl text-blue-600 dark:bg-blue-950/40 dark:text-blue-300">
              <FiNavigationIcon />
            </div>

            <div>
              <h1 className="text-2xl font-bold text-slate-950 dark:text-white">
                Track Your Shipment
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Search the system using a container number, B/L number, booking
                number, vessel, or ShipsGo request ID.
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSearch}
            className="mt-7 flex flex-col gap-3 sm:flex-row"
          >
            <div className="relative flex-1">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Enter container, BL, booking, vessel, or request ID"
                className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="inline-flex min-w-[160px] items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              <FiSearch className={loading ? "animate-pulse" : ""} />
              {loading ? "Searching..." : "Track Shipment"}
            </button>
          </form>

          {recentNumbers.length ? (
            <div className="mt-4">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Recent tracking numbers
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {recentShipments.map((shipment) => (
                  <button
                    key={shipment.id}
                    type="button"
                    onClick={() => {
                      setQuery(getTrackingNo(shipment));
                      openShipment(shipment);
                    }}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-600 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-blue-500 dark:hover:text-blue-300"
                  >
                    {getTrackingNo(shipment)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
            <FiAlertCircle className="mt-0.5 shrink-0" />
            {error}
          </div>
        ) : null}

        {searched && !loading ? (
          <div className="mt-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  Search Results
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {results.length} matching shipment
                  {results.length === 1 ? "" : "s"} found
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/tracking")}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Open Live Map
              </button>
            </div>

            {results.length ? (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {results.map((shipment) => {
                  const status = getTrackingStatus(shipment);

                  return (
                    <button
                      key={shipment.id}
                      type="button"
                      onClick={() => openShipment(shipment)}
                      className="group rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:hover:border-blue-600"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300">
                            <FiBox />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-bold text-slate-900 dark:text-slate-100">
                              {getTrackingNo(shipment)}
                            </p>
                            <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                              {shipment.shippingLine || "Carrier not available"}
                            </p>
                          </div>
                        </div>

                        <span
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusBadgeClass(
                            status.group,
                          )}`}
                        >
                          {status.label}
                        </span>
                      </div>

                      <div className="mt-5 grid gap-3 sm:grid-cols-2">
                        <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-950">
                          <p className="text-[11px] uppercase tracking-wide text-slate-400">
                            Route
                          </p>
                          <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {getOriginPort(shipment)} →{" "}
                            {getDestinationPort(shipment)}
                          </p>
                        </div>

                        <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-950">
                          <p className="text-[11px] uppercase tracking-wide text-slate-400">
                            Vessel / ETA
                          </p>
                          <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {getVesselName(shipment)} • {formatDate(getEta(shipment))}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between text-sm font-semibold text-blue-600 dark:text-blue-400">
                        <span>View on live map</span>
                        <FiArrowRight className="transition group-hover:translate-x-1" />
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="mt-4 grid min-h-[260px] place-items-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-950">
                <div>
                  <FiMapPin className="mx-auto text-4xl text-slate-400" />
                  <p className="mt-4 font-semibold text-slate-900 dark:text-slate-100">
                    No shipment found
                  </p>
                  <p className="mt-1 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Check the container, B/L, booking, vessel, or request ID and
                    search again.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {!searched ? (
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <InfoCard
              icon={<FiBox />}
              title="Container number"
              text="Search using a complete container number such as CSGU6199919."
            />
            <InfoCard
              icon={<FiMapPin />}
              title="Booking or B/L"
              text="Use the booking or bill of lading number stored in the system."
            />
            <InfoCard
              icon={<FiClock />}
              title="Latest saved update"
              text="The result opens the shipment on the live map with the latest stored ShipsGo data."
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FiNavigationIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="h-6 w-6"
      aria-hidden="true"
    >
      <path
        d="M21 3L9.8 14.2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M21 3L14.5 21L9.8 14.2L3 9.5L21 3Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoCard({ icon, title, text }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid h-10 w-10 place-items-center rounded-2xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        {icon}
      </div>
      <h3 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">
        {title}
      </h3>
      <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
        {text}
      </p>
    </div>
  );
}
